import { InfosPSBEN } from '@/store/infosPSBEN/types';
import { ParametresUtilisateur } from '@/store/parametresUtilisateur/types';
import {
	CategorieLieuType, InjectionType, Ps, VaccinationFolder, VaccinType
} from '@/store/folder/types';
import dayjs from 'dayjs';
import _ from 'lodash';
import { DetailsLocalisationData, DetailsVaccinData } from './types';
import { formatStandardStringToDate, formatDateStandard, formatDateWithOrigin, formatDateSchedule } from '@/services/dateFormatter';

export const valeurVaccinParDefaut: VaccinType = {
	vaccinId: -1,
	nom: '',
	voieAdministration: 'injection',
	nbInjections: 0,
	dureeEntreInjections: 0,
	descriptif: '',
	actif: true,
	dateDebutValidite: '',
	dateFinValidite: '',
	margeInjection: 0,
	nomLong: '',
	lots: []
};

export function concatFormAndInfosPSBENToVaccination(
	detailsVaccinForm: DetailsVaccinData,
	detailsLocalisationForm: DetailsLocalisationData,
	infosPsben: InfosPSBEN,
	prescriptionId: number,
	parametresUtilisateur: ParametresUtilisateur | undefined,
	injectionId?: number): InjectionType {
	const cloneVaccinForm = _.cloneDeep(detailsVaccinForm);
	const cloneLocalisationForm = _.cloneDeep(detailsLocalisationForm);
	return {
		vaccinateur: adapteFormVaccinateurPs(infosPsben, parametresUtilisateur),
		horaireVaccination: cloneVaccinForm.horaireVaccination,
		vaccin: cloneVaccinForm.vaccin,
		accordConditionTransmission: true,
		categorieLieu: cloneLocalisationForm.lieuVaccination,
		dateEnregistrement: dayjs().format('DD/MM/YYYY HH:mm'),
		dateRealisation: cloneVaccinForm.dateInjection,
		dateProchaineInjection: dateProchaineInjection(detailsVaccinForm),
		finess: _.trim(cloneLocalisationForm.numFiness).length === 9 ? cloneLocalisationForm.numFiness : '',
		codeEtablissement : cloneLocalisationForm.codeScolaire.toUpperCase(),
		injectionId,
		numeroLot: cloneVaccinForm.lot!.toUpperCase(),
		prescriptionId,
		rangVaccinal: rangVaccinal(detailsVaccinForm),
		siret: _.trim(cloneLocalisationForm.numFiness).length === 14 ? cloneLocalisationForm.numFiness : '',
		codePostal: cloneLocalisationForm.codePostal,
		siteInjection: cloneVaccinForm.zoneVaccination,
		voieAdministration: cloneVaccinForm.vaccin.voieAdministration,
		horodateEnregistrementMaj: undefined,
		horodatageEnregistrement: undefined,
		topEnregistrementMaj: false,
		injectionSupplementaire: false,
		motifInjectionSupp: null,
		psDelegation: null
	};
}

export function dateProchaineInjection(cloneform: DetailsVaccinData): string {
	return formatDateStandard(formatStandardStringToDate(cloneform.dateInjection).add(
		cloneform.vaccin.dureeEntreInjections, 'day'));
}

export function rangVaccinal(cloneform: DetailsVaccinData): number {
	return _.get(cloneform, 'stadeVaccination.stade');
}
function getStadeVaccination(rang: number): string {
	return rang === 1 ? '1ère vaccination' : rang + 'ème vaccination';
}

export function getAlreadySavedDetailsVaccin(currentInjection: InjectionType): DetailsVaccinData {
	const cloneInjection = _.cloneDeep(currentInjection);
	return {
		dateInjection: formatDateWithOrigin(cloneInjection.dateRealisation, 'YYYY-MM-DDTHH:mm:ss', 'DD/MM/YYYY'),
		horaireVaccination: cloneInjection.horaireVaccination,
		lot: cloneInjection.numeroLot,
		stadeVaccination: {
			stade: cloneInjection.rangVaccinal,
			libelle: getStadeVaccination(cloneInjection.rangVaccinal)
		},
		vaccin: cloneInjection.vaccin,
		zoneVaccination: cloneInjection.siteInjection
	};
}

export function getAlreadySavedDetailsLocalisation(currentInjection: InjectionType): DetailsLocalisationData {
	const cloneInjection = _.cloneDeep(currentInjection);
	return {
		codePostal: cloneInjection.codePostal,
		lieuVaccination: cloneInjection.categorieLieu,
		centreVaccination: {
			categorieLieu: {
				categorieLieuId: -1,
				libelle: ''
			},
			code: '',
			codePostal: '',
			libelle: '',
			libelleCommune: '',
			lieuId: 0,
			libelleAffichage: '',
			dateFermeture: ''
		},
		numFiness: _.isEmpty(cloneInjection.finess) ? cloneInjection.siret : cloneInjection.finess,
		codeScolaire: cloneInjection.codeEtablissement
	};
}

export function getInitialDetailsVaccin(
	params: ParametresUtilisateur | undefined,
	lastInjection: InjectionType | undefined,
	listVaccins: VaccinType[]): DetailsVaccinData {
	const dateInjection = _.get(params, 'dateInjection') || formatDateStandard(dayjs());
	let vaccin;
	let stadeVaccination;
	const horaireVaccination = formatDateSchedule(dayjs());
	const zoneVaccination = 'Bras gauche';
	if (lastInjection === undefined) {
		vaccin = _.find(listVaccins, _.pick(params, 'vaccinId') as VaccinType) || initVaccin(listVaccins);
		stadeVaccination = { stade: 1, libelle: '1ère vaccination' };
	} else {
		vaccin = _.find(listVaccins, { vaccinId: _.get(params, 'vaccinId') }) || _.get(lastInjection, 'vaccin');
		vaccin = { ...vaccin, voieAdministration: _.get(lastInjection, 'voieAdministration') };
		const nextRang = _.get(lastInjection, 'rangVaccinal') + 1;
		stadeVaccination = { stade: nextRang, libelle: nextRang + 'ème vaccination' };
	}
	return {
		vaccin,
		lot: '',
		stadeVaccination,
		dateInjection,
		horaireVaccination,
		zoneVaccination
	};
}

export function getInitialDetailsLocalisation(
	params: ParametresUtilisateur | undefined,
	listCategoriesLieuVaccination: CategorieLieuType[]): DetailsLocalisationData {
	const defaultCategorieLieu: CategorieLieuType = _.find(listCategoriesLieuVaccination, {
		categorieLieuId: 7
	}) || listCategoriesLieuVaccination[0];
	const defaultNumFiness = params && params.numFiness ? params.numFiness : '';
	const lieuVaccination = params ?
		(_.find(listCategoriesLieuVaccination, _.pick(params, 'categorieLieuId') as CategorieLieuType)
			|| defaultCategorieLieu) :
		defaultCategorieLieu;
	return {
		lieuVaccination,
		centreVaccination: {
			code: '',
			codePostal: '',
			libelle: '',
			lieuId: -1,
			libelleCommune: '',
			categorieLieu: {
				categorieLieuId: -1,
				libelle: ''
			},
			libelleAffichage: '',
			dateFermeture: ''
		},
		codePostal: '',
		numFiness: defaultNumFiness,
		codeScolaire: ''
	};
}

function initVaccin(listVaccins: VaccinType[]): VaccinType {
	return _.size(listVaccins) === 1 ? listVaccins[0] : valeurVaccinParDefaut;
}

export function adapteFormVaccinateurPs(
	infosPsben: InfosPSBEN,
	parametresUtilisateur: ParametresUtilisateur | undefined): Ps {
	const paramAm: string = _.get(parametresUtilisateur, 'numAM') || '';
	return {
		amFiness: _.isEmpty(paramAm) ? infosPsben.ps.numAM : paramAm,
		categorie: infosPsben.ps.categorie,
		informationContact: infosPsben.ps.mail,
		nom: infosPsben.ps.nom,
		organismeRattachement: '',
		prenom: infosPsben.ps.prenom,
		origineDonnees: 'connexion',
		psId: undefined,
		rpps: infosPsben.ps.numRPPS,
		specialite: infosPsben.ps.specialite
	};
}
